import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "../../site/ui";
import { fmtDate } from "../../site/pages/Forum";
import { PROJECT_STATUS } from "../data";
import { useCart } from "../../site/cart";
import { FW_STATUS_TONE } from "./LaonaFirmware";
import CartDialog from "./LaonaOrder";
import { RecipeDialog } from "./LaonaRecipe";

function makeApi(base) {
  return async function call(path = "", options = {}) {
    const res = await fetch(`${base}${path}`, {
      credentials: "same-origin",
      headers: options.body ? { "Content-Type": "application/json" } : undefined,
      ...options,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = new Error(typeof data.detail === "string" ? data.detail : "요청을 처리하지 못했습니다.");
      err.status = res.status;
      throw err;
    }
    return data;
  };
}

/* 라오나봇 프로젝트 API (백엔드 app/laona_projects.py) */
export const projectApi = makeApi("/api/laona/projects");
/* 수동 프로젝트 API (백엔드 app/laona_manual.py) — PinWire 로 직접 그리는 프로젝트 */
export const manualApi = makeApi("/api/laona/manual");

export const won = (n) => `${Number(n || 0).toLocaleString("ko-KR")}원`;

export function StatusTag({ status }) {
  const s = PROJECT_STATUS[status] || { label: status, tone: "" };
  return <span className={`lb-tag lb-tag--st lb-tag--${s.tone}`}>{s.label}</span>;
}

const fileSafe = (s) => s.replace(/[\\/:*?"<>|]+/g, "_").trim() || "project";

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* 서버가 만든 파일(BOM CSV)을 그대로 내려받는다 */
async function downloadFile(url, name) {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(typeof d.detail === "string" ? d.detail : "파일을 받지 못했습니다.");
  }
  saveBlob(await res.blob(), name);
}

/* 서버에 저장된 SVG(도구가 저장할 때 함께 보낸 그림)를 받아 PNG 로 바꿔 내려받는다 */
async function downloadPng(url, name) {
  const res = await fetch(url, { credentials: "same-origin" });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(typeof d.detail === "string" ? d.detail : "그림을 받지 못했습니다.");
  }
  const src = URL.createObjectURL(new Blob([await res.text()], { type: "image/svg+xml" }));
  try {
    const img = await new Promise((ok, no) => {
      const im = new Image();
      im.onload = () => ok(im);
      im.onerror = () => no(new Error("그림을 PNG로 바꾸지 못했습니다."));
      im.src = src;
    });
    const k = Math.max(img.naturalWidth, img.naturalHeight) * 2 > 8000 ? 1 : 2; // 아주 큰 그림은 1배로
    const cv = document.createElement("canvas");
    cv.width = img.naturalWidth * k;
    cv.height = img.naturalHeight * k;
    const g = cv.getContext("2d");
    g.scale(k, k);
    g.drawImage(img, 0, 0);
    const blob = await new Promise((ok) => cv.toBlob(ok, "image/png"));
    if (!blob) throw new Error("그림을 PNG로 바꾸지 못했습니다.");
    saveBlob(blob, name);
  } finally {
    URL.revokeObjectURL(src);
  }
}

/* 꺼진 버튼의 까닭 — 마우스를 올리거나 누르려 하면 마우스 끝에 안내를 띄운다.
   꺼진(disabled) 버튼은 마우스 이벤트를 받지 않으므로 감싼 span 이 받는다 (버튼은 pointer-events: none)
   wide 면 긴 설명용 — 여러 줄로 감싸고 커서는 바꾸지 않는다 (목록 제목의 설명) */
function LockedTip({ tip, children, wide = false }) {
  const [at, setAt] = useState(null); // { x, y } — 안내를 띄울 곳
  const timer = useRef(0);
  useEffect(() => () => clearTimeout(timer.current), []);
  const show = (e) => { clearTimeout(timer.current); setAt({ x: e.clientX, y: e.clientY }); };
  const hide = () => { clearTimeout(timer.current); setAt(null); };
  return (
    <span
      className={wide ? "lb-hovertip" : "lb-locked"}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") show(e); }}
      onPointerMove={(e) => { if (e.pointerType === "mouse") show(e); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") hide(); }}
      onClick={(e) => { show(e); timer.current = setTimeout(() => setAt(null), 2500); }} // 휴대폰은 누르면 잠깐
    >
      {children}
      {at && (
        <span className={`lb-cursortip${wide ? " lb-cursortip--wide" : ""}`} role="tooltip"
          style={{ left: Math.max(8, Math.min(at.x + 14, window.innerWidth - (wide ? 348 : 240))), top: at.y + 18 }}>
          {tip}
        </span>
      )}
    </span>
  );
}

/* 주문이 들어간 상태 — 펌웨어 개발의뢰가 주문에 담긴 뒤(app/laona_fw.py LABEL), 라오나봇 프로젝트는 주문 상태를 따른다(sync_order) */
const FW_ORDERED = ["ORDERED", "RECEIVED", "IN_PROGRESS", "DONE"];
const BOT_ORDERED = ["ORDERED", "PAID", "SHIPPED", "DELIVERED"];

/* 목록 한 줄 — 제목을 누르면 라오나 에디터로 연다.
   윗줄: [자료 다운로드] · [장바구니에 담기], 아랫줄: below(수동 프로젝트의 [레시피 공개하기]) · [수정] · [삭제].
   [수정]은 바로 라오나 에디터로 넘어간다 — 프로젝트 이름은 만든 뒤 바꿀 수 없다.
   onCart 가 있으면 [장바구니에 담기]를 둔다 — 부품 목록 · 금액 · 펌웨어 개발 옵션 팝업(LaonaOrder.jsx)이 뜬다.
   (2026-11 — 예전 [펌웨어 개발의뢰] · [주문하기]를 이 버튼 하나로 합쳤다)
   downloadLocked 가 있으면 [자료 다운로드]를 끄고, 마우스를 올리면 그 까닭을 마우스 끝에 띄운다 (수동 프로젝트는 주문 전까지).
   inCart 가 참이면(이 프로젝트에서 담은 상품이 장바구니에 있으면) 장바구니 · MY 페이지에서 본다고 알리고,
   아니고 ordered 가 참이면(취소하지 않은 주문이 있으면) 주문내역은 MY 페이지(/my)에서 본다고 알린다 — 같은 자리 */
function ProjectRow({ title, to, tags, meta, onDelete, download, onCart, downloadLocked, below, ordered, inCart }) {
  const [open, setOpen] = useState(false); // 자료 다운로드 펼침
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <li className="lb-prow">
      <div className="lb-prow__top">
        <Link to={to} className="lb-prow__title">
          {tags}
          <b>{title}</b>
        </Link>
        <div className="lb-prow__actions">
          {downloadLocked ? (
            <LockedTip tip={downloadLocked}>
              <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" disabled aria-label={`자료 다운로드 — ${downloadLocked}`}>자료 다운로드</button>
            </LockedTip>
          ) : (
            <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" aria-expanded={open}
                    onClick={() => { setError(""); setOpen((v) => !v); }}>자료 다운로드</button>
          )}
          {onCart && <button type="button" className="lb-btn lb-btn--sm lb-btn--primary" onClick={onCart}>장바구니에 담기</button>}
        </div>
      </div>
      <div className="lb-prow__meta">{meta}</div>
      {inCart ? (
        <p className="lb-prow__order">
          <Link to="/cart">장바구니</Link>에 담긴 상품은 아이닷메타 홈페이지 <Link to="/my">MY 페이지</Link>에서 확인할 수 있습니다.
        </p>
      ) : ordered && (
        <p className="lb-prow__order">
          주문내역은 아이닷메타 홈페이지 <Link to="/my">MY 페이지</Link>에서 확인할 수 있습니다.
        </p>
      )}
      <div className="lb-prow__below">
        {below}
        <Link to={to} className="lb-btn lb-btn--sm">수정</Link>
        <button type="button" className="lb-btn lb-btn--sm lb-btn--danger" disabled={busy} onClick={() => run(onDelete)}>삭제</button>
      </div>

      {open && !downloadLocked && <div className="lb-prow__panel">{download(run, busy)}</div>}
      {error && <p className="lb-error lb-prow__error">{error}</p>}
    </li>
  );
}

/* 레시피 공개 — [완료]로 결선도 · 배선도를 저장한 프로젝트만. 공개 중이면 보기 · 고치기 · 내리기 */
function RecipeControls({ p, onOpen, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!p.recipe) {
    const ready = p.has_diagram && p.has_bread;
    return ready ? (
      <button type="button" className="lb-btn lb-btn--sm lb-btn--recipe" onClick={onOpen}>레시피 공개하기</button>
    ) : (
      <LockedTip tip="회로설계 · 브레드보드 배선을 마치고 [완료]를 누르면 공개할 수 있습니다">
        <button type="button" className="lb-btn lb-btn--sm lb-btn--recipe" disabled>레시피 공개하기</button>
      </LockedTip>
    );
  }
  async function unpublish() {
    if (!window.confirm(`'${p.recipe.title}' 레시피를 갤러리에서 내릴까요?\n프로젝트는 그대로 남고, 언제든 다시 공개할 수 있습니다.`)) return;
    setBusy(true);
    try { await manualApi(`/${p.id}/recipe`, { method: "DELETE" }); await onChanged(); } finally { setBusy(false); }
  }
  return (
    <>
      <span className="lb-tag lb-tag--st lb-tag--done" title={`조회 ${p.recipe.views}`}>레시피 공개 중</span>
      <Link to={`/laonabot/recipes/${p.id}`} className="lb-btn lb-btn--sm">레시피 보기</Link>
      <button type="button" className="lb-btn lb-btn--sm" onClick={onOpen} disabled={busy}>고치기</button>
      <button type="button" className="lb-btn lb-btn--sm" onClick={unpublish} disabled={busy}>공개 내리기</button>
    </>
  );
}

function Column({ title, desc, count, children, empty }) {
  return (
    <section className="lb-pcol" aria-label={title}>
      {/* 설명은 제목에 마우스를 올리면 마우스 끝에 띄운다 */}
      <header className="lb-pcol__head">
        <h2><LockedTip tip={desc} wide>{title}</LockedTip> <span className="lb-pcol__count">{count}</span></h2>
      </header>
      {count ? <ul className="lb-plist">{children}</ul> : empty}
    </section>
  );
}

/* 내 프로젝트 — 왼쪽은 직접 만든(수동) 프로젝트, 오른쪽은 라오나봇 프로젝트 */
export default function LaonaProjects() {
  const [all, setAll] = useState(false);
  const [bot, setBot] = useState({ status: "loading", items: [], admin: false });
  const [manual, setManual] = useState({ status: "loading", items: [] });
  const [carting, setCarting] = useState(null);   // 장바구니에 담기 팝업을 띄운 프로젝트 (라오나봇 프로젝트는 kind "bot")
  const [recipe, setRecipe] = useState(null);     // 레시피 공개 팝업을 띄운 수동 프로젝트
  const cart = useCart();
  /* 이 프로젝트에서 담은 상품이 장바구니에 있는지 — 수동은 부품(프로젝트 번호) 또는 펌웨어, 라오나봇은 펌웨어 의뢰로 본다 */
  const fwInCart = (p) => !!p.fw && cart.fw.some((r) => r.id === p.fw.id);

  useSeo({ title: "내 프로젝트 — LaonaBot", path: "/laonabot/projects" });

  /* 관리자 '전체 보기' 를 빨리 바꿔 누르면 늦게 온 응답이 지금 토글과 다른 목록을 덮지 않게 — 마지막 요청만 반영 */
  const botSeq = useRef(0);
  const loadBot = () => {
    const n = ++botSeq.current;
    return projectApi(all ? "?all=1" : "")
      .then((d) => { if (n === botSeq.current) setBot({ status: "ready", items: d.items, admin: d.admin }); })
      .catch(() => { if (n === botSeq.current) setBot({ status: "error", items: [], admin: false }); });
  };
  const loadManual = () =>
    manualApi("")
      .then((d) => setManual({ status: "ready", items: d.items }))
      .catch(() => setManual({ status: "error", items: [] }));

  useEffect(() => { loadManual(); }, []);
  useEffect(() => {
    setBot((s) => ({ ...s, status: "loading" }));
    loadBot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all]);

  const state = (s, n) => (s.status === "loading" ? <p className="lb-empty">불러오는 중입니다…</p>
    : s.status === "error" ? <p className="lb-empty">프로젝트를 불러오지 못했습니다.</p> : n);

  return (
    <section className="lb-section lb-section--top">
      <div className="lb-wrap">
        <header className="lb-head lb-head--row">
          <div>
            <p className="lb-kicker">PROJECTS</p>
            <h1>내 프로젝트</h1>
            <p className="lb-lead">직접 만든 프로젝트와 라오나봇 프로젝트를 한곳에서 관리합니다.</p>
          </div>
          <div className="lb-head__tools">
            {bot.admin && (
              <button type="button" className="lb-btn" onClick={() => setAll((v) => !v)}>
                {all ? "라오나봇: 내 프로젝트만" : "라오나봇: 전체 보기 (관리자)"}
              </button>
            )}
            {bot.admin && <Link to="/laonabot/admin/builds" className="lb-btn">빌드 검수</Link>}
            <Link to="/laonabot/start" className="lb-btn lb-btn--primary">새 프로젝트</Link>
          </div>
        </header>

        <div className="lb-pcols">
          <Column
            title="수동 프로젝트"
            desc="라오나 에디터로 직접 그린 프로젝트입니다. 부품을 주문하면 결선도 · 배선도 그림을 받을 수 있고, 기능정의서를 쓰면 펌웨어 개발을 의뢰할 수 있습니다."
            count={manual.items.length}
            empty={state(manual, (
              <div className="lb-empty lb-empty--box">
                <b>아직 수동 프로젝트가 없습니다</b>
                <p>모듈을 직접 배치하고 배선해 보세요. 무료입니다.</p>
                <div className="lb-actions lb-actions--center">
                  <Link to="/laonabot/start?way=manual" className="lb-btn">직접 만들기</Link>
                </div>
              </div>
            ))}
          >
            {manual.items.map((p) => (
              <ProjectRow
                key={p.id}
                title={p.title}
                to={`/laonabot/wiring?manual=${p.id}`}
                tags={p.fw && <span className={`lb-tag lb-tag--st lb-tag--${FW_STATUS_TONE[p.fw.status] || ""}`}>펌웨어 {p.fw.label}</span>}
                meta={<><span>수정 {fmtDate(p.updated_at || p.created_at)}</span></>}
                onCart={() => setCarting(p)}
                inCart={cart.projects.includes(p.id) || fwInCart(p)}
                downloadLocked={p.ordered ? "" : "주문 후 다운로드 가능합니다"}
                ordered={p.ordered || FW_ORDERED.includes(p.fw?.status)}
                below={<RecipeControls p={p} onOpen={() => setRecipe(p)} onChanged={loadManual} />}
                onDelete={async () => {
                  if (!window.confirm(`'${p.title}' 프로젝트를 삭제할까요?\n연결도 · 브레드보드 작업이 모두 지워지고 되돌릴 수 없습니다.`)) return;
                  await manualApi(`/${p.id}`, { method: "DELETE" });
                  await loadManual();
                }}
                download={(run, busy) => {
                  const base = `/api/laona/manual/${p.id}`, name = fileSafe(p.title);
                  const png = (path, label) => () => run(() => downloadPng(`${base}/${path}`, `${name}-${label}.png`));
                  const bread = p.has_bread && p.has_bread_dim && p.has_bread_wires;
                  return (
                    <>
                      <div className="lb-prow__line">
                        <button type="button" className="lb-btn lb-btn--sm" disabled={busy || !p.has_diagram} onClick={png("diagram.svg", "결선도")}>
                          결선도 (회로도) PNG
                        </button>
                        <button type="button" className="lb-btn lb-btn--sm" disabled={busy || !p.has_diagram}
                          onClick={() => run(() => downloadFile(`${base}/bom.csv`, `${name}-BOM.csv`))}>
                          전체 BOM (CSV)
                        </button>
                      </div>
                      <div className="lb-prow__line">
                        <button type="button" className="lb-btn lb-btn--sm" disabled={busy || !p.has_bread} onClick={png("bread.svg", "배선도-모두")}>
                          배선도 (모두) PNG
                        </button>
                        <button type="button" className="lb-btn lb-btn--sm" disabled={busy || !p.has_bread_dim} onClick={png("bread-dim.svg", "배선도-모듈반투명")}>
                          배선도 (모듈 반투명) PNG
                        </button>
                        <button type="button" className="lb-btn lb-btn--sm" disabled={busy || !p.has_bread_wires} onClick={png("bread-wires.svg", "배선도-배선만")}>
                          배선도 (배선만) PNG
                        </button>
                      </div>
                      {(!p.has_diagram || !bread) && (
                        <p className="lb-prow__hint">
                          {!p.has_diagram && "결선도 · BOM은 연결도 도구에서 연결도를 그리고 [완료]를 누르면 생깁니다. "}
                          {!p.has_bread && "배선도는 브레드보드 탭에서 ‘배선 시작’까지 마치고 [완료]를 누르면 생깁니다."}
                          {p.has_bread && !bread && "배선도 세 가지가 모두 없으면 연결도 도구에서 배선을 마치고 [완료]를 다시 누르면 생깁니다."}
                        </p>
                      )}
                    </>
                  );
                }}
              />
            ))}
          </Column>

          <Column
            title={all ? "라오나봇 프로젝트 (전체)" : "라오나봇 프로젝트"}
            desc="라오나봇과 대화로 설계하고 내 프로젝트로 저장한 프로젝트입니다. [장바구니에 담기]로 부품과 펌웨어 개발 · 빌드를 함께 담아 주문합니다."
            count={bot.items.length}
            empty={state(bot, (
              <div className="lb-empty lb-empty--box">
                <b>아직 라오나봇 프로젝트가 없습니다</b>
                <p>라오나봇과 대화로 첫 시제품을 설계해 보세요.</p>
                <div className="lb-actions lb-actions--center">
                  <Link to="/laonabot/start?way=bot" className="lb-btn lb-btn--primary">라오나봇과 만들기</Link>
                </div>
              </div>
            ))}
          >
            {bot.items.map((p) => (
              <ProjectRow
                key={p.id}
                title={p.title}
                to={p.status === "MANUAL" && p.manual_id ? `/laonabot/wiring?manual=${p.manual_id}` : `/laonabot/projects/${p.id}`}
                tags={<>
                  <StatusTag status={p.status} />
                  {p.fw && p.status !== "APPROVED" && (
                    <span className={`lb-tag lb-tag--st lb-tag--${FW_STATUS_TONE[p.fw.status] || ""}`}>펌웨어 {p.fw.label}</span>
                  )}
                </>}
                /* [내 프로젝트로 저장]한 라오나봇 프로젝트 — 부품과 펌웨어 개발 · 빌드를 함께 주문한다 */
                onCart={p.status === "APPROVED" && p.fw && p.fw.status === "REQUESTED" ? () => setCarting({ ...p, kind: "bot" }) : undefined}
                inCart={fwInCart(p)}
                ordered={BOT_ORDERED.includes(p.status) || FW_ORDERED.includes(p.fw?.status)}
                meta={<>
                  {all && p.member && <span>{p.member.name} · {p.member.email}</span>}
                  {p.status !== "MANUAL" && (
                    <span>
                      {p.plan?.paid ? "이용료 결제됨"
                        : p.plan?.pay_requested ? "이용료 입금 대기"
                        : `무료 사용 ${won(Math.min(p.usage.krw, p.plan?.free_krw ?? 0))}/${won(p.plan?.free_krw)}`}
                      {all && ` · 사용 ${won(p.usage.krw)}`}
                    </span>
                  )}
                  <span>{fmtDate(p.updated_at || p.created_at)}</span>
                </>}
                onDelete={async () => {
                  if (!window.confirm(`'${p.title}' 프로젝트를 삭제할까요?\n내 프로젝트에서 사라지고 대화 · 설계를 다시 열 수 없습니다. 이용료 · 주문 기록은 회사에 남습니다.`)) return;
                  await projectApi(`/${p.id}`, { method: "DELETE" });
                  await loadBot();
                }}
                download={() => (p.status === "MANUAL" ? (
                  <p className="lb-prow__hint">
                    직접 만들기로 넘긴 프로젝트입니다. 왼쪽 수동 프로젝트에서 배선 · 부품 주문을 이어가고, 펌웨어는 스스로 빌드해 주세요.
                  </p>
                ) : p.zip ? (
                  <div className="lb-prow__line">
                    <a className="lb-btn lb-btn--sm lb-btn--primary" href={`/api/laona/projects/${p.id}/package.zip`} download>
                      전체 자료 ZIP 받기
                    </a>
                    <span className="lb-prow__hint">소스 코드 · 펌웨어 · 기능 명세 · 배선도 PDF · 연결 리스트 · 부품 목록</span>
                  </div>
                ) : (
                  <p className="lb-prow__hint">
                    {p.fw
                      ? `펌웨어 개발의뢰 ${p.fw.request_no} — ${p.fw.label}. 펌웨어는 바이너리를 포함해 코드 전체를 제공합니다.`
                      : "라오나봇과 설계 · 기능 명세를 마치고 내 프로젝트로 저장한 뒤 [장바구니에 담기]로 부품과 펌웨어 개발 · 빌드를 담아 주문할 수 있습니다."}
                  </p>
                ))}
              />
            ))}
          </Column>
        </div>
      </div>
      {carting && (
        <CartDialog project={carting} onClose={() => setCarting(null)}
                    onDone={carting.kind === "bot" ? undefined : loadManual /* 새로 만든 펌웨어 의뢰가 카드에 반영되게 */} />
      )}
      {recipe && <RecipeDialog project={recipe} onClose={() => setRecipe(null)} onSaved={loadManual} />}
    </section>
  );
}
